// UserDetails/BankDetails.jsx

import React, { useEffect, useState } from "react";

export default function BankDetails({ shop, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    accountHolderName: shop?.bankDetails?.accountHolderName || '',
    bankName: shop?.bankDetails?.bankName || '',
    accountNumber: shop?.bankDetails?.accountNumber || '',
    ifscCode: shop?.bankDetails?.ifscCode || ''
  });

  // Sync bank details form when active shop changes, even during edit mode
  useEffect(() => {
    setEditData({
      accountHolderName: shop?.bankDetails?.accountHolderName || '',
      bankName: shop?.bankDetails?.bankName || '',
      accountNumber: shop?.bankDetails?.accountNumber || '',
      ifscCode: shop?.bankDetails?.ifscCode || ''
    });
    // Keep isEditing as-is to preserve edit mode state
  }, [shop]);

  const hasBankDetails = shop?.bankDetails && (
    shop.bankDetails.accountHolderName ||
    shop.bankDetails.bankName ||
    shop.bankDetails.accountNumber ||
    shop.bankDetails.ifscCode
  );

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({
      accountHolderName: shop?.bankDetails?.accountHolderName || '',
      bankName: shop?.bankDetails?.bankName || '',
      accountNumber: shop?.bankDetails?.accountNumber || '',
      ifscCode: shop?.bankDetails?.ifscCode || ''
    });
  };

  const handleSave = async () => {
    try {
      await onUpdate(editData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update bank details:', error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      accountHolderName: shop?.bankDetails?.accountHolderName || '',
      bankName: shop?.bankDetails?.bankName || '',
      accountNumber: shop?.bankDetails?.accountNumber || '',
      ifscCode: shop?.bankDetails?.ifscCode || ''
    });
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  if (isEditing) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-800">Bank Details</h2>
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Holder Name
            </label>
            <input
              type="text"
              value={editData.accountHolderName}
              onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter account holder name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank Name
            </label>
            <input
              type="text"
              value={editData.bankName}
              onChange={(e) => handleInputChange('bankName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter bank name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Number
            </label>
            <input
              type="text"
              value={editData.accountNumber}
              onChange={(e) => handleInputChange('accountNumber', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter account number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              IFSC Code
            </label>
            <input
              type="text"
              value={editData.ifscCode}
              onChange={(e) => handleInputChange('ifscCode', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter IFSC code"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-800">Bank Details</h2>
        {shop && (
          <button
            onClick={handleEdit}
            className="px-3 py-1 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors text-xs font-medium"
          >
            ✏️ Edit
          </button>
        )}
      </div>
      
      {hasBankDetails ? (
        <div className="space-y-3 text-sm">
          {shop.bankDetails.accountHolderName && (
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="font-medium text-gray-600">Account Holder:</span>
              <span className="text-gray-800 font-semibold">{shop.bankDetails.accountHolderName}</span>
            </div>
          )}
          {shop.bankDetails.bankName && (
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="font-medium text-gray-600">Bank Name:</span>
              <span className="text-gray-800 font-semibold">{shop.bankDetails.bankName}</span>
            </div>
          )}
          {shop.bankDetails.accountNumber && (
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="font-medium text-gray-600">Account Number:</span>
              <span className="text-gray-800 font-mono text-xs">{shop.bankDetails.accountNumber}</span>
            </div>
          )}
          {shop.bankDetails.ifscCode && (
            <div className="flex justify-between items-center py-2">
              <span className="font-medium text-gray-600">IFSC Code:</span>
              <span className="text-gray-800 font-mono text-xs">{shop.bankDetails.ifscCode}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6">
          <div className="text-gray-400 text-sm">
            {shop ? "No bank details available for this shop" : "Select a shop to view bank details"}
          </div>
        </div>
      )}
    </div>
  );
}
